import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-md space-y-6 rounded-lg border border-destructive/50 bg-card p-6 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="rounded-pill bg-destructive/10 p-3">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-foreground">
                  Unexpected Error
                </h1>
                <p className="text-sm text-muted-foreground">
                  Something went wrong
                </p>
              </div>
            </div>

            {this.state.error && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  Error Details:
                </p>
                <div className="rounded-md bg-muted p-3">
                  <p className="wrap-break-word text-xs font-mono text-muted-foreground">
                    {this.state.error.message}
                  </p>
                </div>
              </div>
            )}

            {
              this.state.errorInfo && (
                <details className="cursor-pointer rounded-md border border-border p-3">
                  <summary className="text-sm font-medium text-foreground">
                    Stack Trace (Dev Only)
                  </summary>
                  <pre className="mt-2 overflow-auto text-xs text-muted-foreground">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

            <div className="flex gap-2">
              <Button
                onClick={this.handleReload}
                className="flex-1"
                variant="default"
              >
                Reload Page
              </Button>
              <Button
                onClick={this.handleReset}
                className="flex-1"
                variant="secondary"
              >
                Try Again
              </Button>
            </div>

            <p className="text-center text-xs text-muted-foreground">
              If this error persists, please contact support
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
